'use client'

import { Spacer, Text } from '@vinted/web-ui'
import { ReactNode, useCallback, useEffect, useState } from 'react'

import {
  businessAccountFromUserDto,
  getEprUinList,
  transformEprUinListDto,
  EprUinModel,
} from '@marketplace-web/business-accounts/business-accounts-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import AddCountryUinBlock from './AddCountryUinBlock'
import UinList from './UinList'
import { EPR_GUIDE_URL } from '../../constants/routes'

const EprSettings = () => {
  const [uinList, setUinList] = useState<Array<EprUinModel>>([])
  const [isUinListLoading, setIsUinListLoading] = useState(false)
  const [uinListError, setUinListError] = useState('')

  const fetchUinList = useCallback(async () => {
    setIsUinListLoading(true)
    const response = await getEprUinList()

    if ('errors' in response) {
      setIsUinListLoading(false)
      setUinListError(response.errors[0]!.value)

      return
    }

    setUinList(transformEprUinListDto(response))
    setIsUinListLoading(false)
  }, [])

  useEffect(() => {
    fetchUinList()
  }, [fetchUinList])

  const translate = useTranslate('epr_settings')
  const { user } = useSession()

  // Domestic EPR will always return 1 country, so set the country title to the first country title
  const countryTitle = uinList.length
    ? uinList[0]!.country_title
    : businessAccountFromUserDto(user)?.address?.country_title
  const allCategoriesSubmitted = uinList.length ? uinList[0]!.all_categories_submitted : false

  const eprLearnMoreLink = {
    'epr-learn-more-link': (chunks: Array<ReactNode>) => (
      <a
        href={EPR_GUIDE_URL}
        data-testid="epr-learn-more-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        {chunks}
      </a>
    ),
  }

  return (
    <>
      <Spacer size="x-large" />
      <Text as="h1" type="heading">
        {translate('title')}
      </Text>
      <Spacer size="medium" />
      <Text as="span" html>
        {translate('subtitle', eprLearnMoreLink)}
      </Text>
      <Spacer size="x3-large" />
      <AddCountryUinBlock
        countryTitle={countryTitle || ''}
        disableAdd={allCategoriesSubmitted}
        refetch={fetchUinList}
      />
      {allCategoriesSubmitted && (
        <>
          <Spacer size="medium" />
          <div className="all-categories-provided">
            <Text as="h3" type="subtitle" theme="success">
              {translate('all_categories_provided')}
            </Text>
          </div>
        </>
      )}
      <Spacer size="x-large" />
      {uinListError && (
        <Text as="h3" type="subtitle">
          {uinListError}
        </Text>
      )}
      {!uinListError && !!uinList.length && (
        <UinList
          uinList={uinList}
          countryTitle={countryTitle || ''}
          isListLoading={isUinListLoading}
          refetch={fetchUinList}
        />
      )}
    </>
  )
}

export default EprSettings
